import React, { useState, createRef, useCallback, useEffect } from "react";
import { get as _get } from "lodash-es";
import ReactAvatarEditor from "react-avatar-editor";
import Dropzone from "react-dropzone";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import { IMG_SIZE } from "./Avatar/styled";
import { axiosClient } from "../redux_axios_middleware";
import { Modal, ModalBody, ModalFooter, ModalTitle } from "./Modal";
import Button from "./Button";

import { commitHuman, updateHuman } from "../actions/humans";

const AvatarEditorModal = ({ human, onClosed }) => {
  const dispatch = useDispatch();
  const [scale, setScale] = useState(1.0);
  const [avatar, setAvatar] = useState(null);
  const dropzoneRef = createRef();
  const avatarRef = createRef();

  const onSaveAvatar = useCallback(() => {
    avatarRef.current.getImageScaledToCanvas().toBlob(
      async function (blob) {
        const formData = new FormData();
        formData.append("image", blob);
        axiosClient({
          method: "POST",
          url: "human-image.json",
          params: { humanId: human.id },
          data: formData,
        })
          .then((response) => {
            const { image, syncedAt } = response.data;
            dispatch(commitHuman(human));
            dispatch(updateHuman(human.id, { image, syncedAt }));
            onClosed();
            if (human.isNew) {
              window.plausible && window.plausible("HumanAdded");
            }
          })
          .catch((error) => {
            const errorMessage = _get(error, "response.data.error");
            if (errorMessage) {
              alert(errorMessage);
            } else {
              alert(`Could not upload the image. ${error}`);
            }
          });
      },
      "image/jpeg",
      0.98,
    );
  }, [human, avatarRef, onClosed, dispatch]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "Enter" && avatar && onSaveAvatar) {
        onSaveAvatar();
      }
    };

    document.addEventListener("keypress", handleKeyPress);
    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  }, [avatar, onSaveAvatar]);

  const openDialog = () => {
    // Note that the ref is set async,
    // so it might be null at some point
    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };

  const onPaste = useCallback((event) => {
    const items = (event.clipboardData || event.originalEvent.clipboardData)
      .items;
    for (let index in items) {
      const item = items[index];
      if (item.kind === "file") {
        setAvatar(item.getAsFile());
        return;
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener("paste", onPaste);
    return () => {
      document.removeEventListener("paste", onPaste);
    };
  }, [onPaste]);

  const onDeleteCurrentAvatar = useCallback(() => {
    axiosClient({
      method: "DELETE",
      url: "human-image.json",
      params: { humanId: human.id },
    })
      .then(() => {
        dispatch(updateHuman(human.id, { image: null }));
        onClosed();
      })
      .catch((error) => {
        alert(`Could not delete the image. ${error}`);
      });
  }, [human, onClosed, dispatch]);

  const img_size = IMG_SIZE * 1.5; // hidpi

  return (
    <Modal isOpened={true} onClose={onClosed}>
      <ModalTitle>Change avatar</ModalTitle>
      <ModalBody>
        {" "}
        <Dropzone
          ref={dropzoneRef}
          multiple={false}
          accept="image/*"
          onDrop={(file) => setAvatar(file[0])}
          noClick // to keep image dragable in the editor
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              {avatar ? (
                <ReactAvatarEditor
                  ref={avatarRef}
                  width={img_size}
                  height={img_size}
                  borderRadius={img_size / 2}
                  image={avatar}
                  scale={scale}
                />
              ) : null}

              {avatar && (
                <div>
                  Zoom:{" "}
                  <input
                    type="range"
                    step="0.01"
                    min="1"
                    max="3"
                    value={scale}
                    onChange={(e) => {
                      setScale(parseFloat(e.target.value));
                    }}
                  />
                </div>
              )}

              <div>
                <Button onClick={openDialog}>
                  {avatar ? "Choose a different file" : "Choose a file"}
                </Button>{" "}
                or paste an image
              </div>
            </div>
          )}
        </Dropzone>
      </ModalBody>
      <ModalFooter>
        <div className="mt-5">
          <Button
            className="mr-3"
            intent="danger"
            onClick={onDeleteCurrentAvatar}
          >
            Delete current avatar
          </Button>{" "}
          <Button onClick={onSaveAvatar} disabled={!avatar}>
            Save
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

AvatarEditorModal.propTypes = {
  human: PropTypes.object,
  onUpdate: PropTypes.func,
  onClosed: PropTypes.func,
};

export default AvatarEditorModal;
